import { formatDateTimeToString, formatDateToString } from "../utils/dateFormat";
import type { ProductGroupDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";
import type { ProductGroupDto as ODataProductGroupDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Odata/V1/models/index";
import type { ProductGroupRow } from "../routes/cooling-regimes-product-group";

/**
 * Lightweight row shape for displaying or selecting product groups.
 * Mirrors the style of ProductRow mapping in productMapper.ts.
 */
export interface ProductGroupListRow {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
  vbnChangeDateTime: string;
}

/**
 * Maps a single ProductGroupDto to a ProductGroupListRow.
 */
export function mapProductGroupDtoToRow(dto: ProductGroupDto): ProductGroupListRow {
  return {
    code: dto.code !== undefined && dto.code !== null ? String(dto.code) : "",
    name: dto.name ?? "",
    startDate: dto.startDate ? formatDateToString(dto.startDate) : "",
    endDate: dto.endDate ? formatDateToString(dto.endDate) : "",
    vbnChangeDateTime: dto.vbnChangeDateTime
      ? formatDateTimeToString(dto.vbnChangeDateTime)
      : "",
  };
}

/**
 * Maps an array of ProductGroupDto to ProductGroupListRow[].
 */
export function mapProductGroupDtoArrayToRows(dtos: ProductGroupDto[]): ProductGroupListRow[] {
  return dtos.map(mapProductGroupDtoToRow);
}

/**
 * Maps ProductGroupDto from the OData ProductGroupOverview endpoint to ProductGroupRow for pagination.
 *
 * Field mappings:
 * - productGroupId (number) -> coolingRegimeProductGroupId (string) - using productGroupId as row identifier
 * - code (number) -> code (number) - product group code
 * - name (string) -> name (string) - product group name
 * - startDate (Date) -> startDate (string formatted as DD-MM-YYYY)
 * - endDate (Date) -> endDate (string formatted as DD-MM-YYYY)
 * - vbnChangeDateTime (Date) -> vbnChangeDateTime (string formatted as DD-MM-YYYY HH:mm)
 */
export function mapODataProductGroupDtoToRow(dto: ODataProductGroupDto): ProductGroupRow {
  return {
    // Ensure a unique and stable row identifier: prefer productGroupId, fallback to code
    coolingRegimeProductGroupId: String(((dto as any).productGroupId ?? dto.code) ?? 0),
    code: dto.code ?? 0,
    name: dto.name ?? `Product Group ${dto.code ?? 0}`,
    startDate: dto.startDate ? formatDateToString(dto.startDate) : "",
    endDate: dto.endDate ? formatDateToString(dto.endDate) : "",
    vbnChangeDateTime: (dto as any).vbnChangeDateTime
      ? formatDateTimeToString((dto as any).vbnChangeDateTime)
      : "",
  };
}

/**
 * Maps an array of OData ProductGroupDto to ProductGroupRow array for pagination.
 */
export function mapODataProductGroupDtoArrayToRows(
  dtos: ODataProductGroupDto[],
): ProductGroupRow[] {
  return dtos.map(mapODataProductGroupDtoToRow);
}
